import { noOpPlayer } from './no-op-player';
import { type AudioPlayer, type PlayOptions, SoundCue } from './types';

/** Procedural Web Audio cue player. Lazy-inits AudioContext on first play (autoplay budget); falls back to no-op when unavailable. */

type SynthCtx = { ac: AudioContext; master: GainNode };

type ToneOpts = {
  freq: number;
  start: number;
  release: number;
  type?: OscillatorType;
  attack?: number;
  peak?: number;
  detune?: number;
  filterHz?: number;
};

const MIN_GAIN = 0.0001;

const playTone = (ac: AudioContext, dest: AudioNode, o: ToneOpts): void => {
  const {
    freq,
    start,
    release,
    type = 'sine',
    attack = 0.005,
    peak = 0.35,
    detune = 0,
    filterHz,
  } = o;
  const osc = ac.createOscillator();
  osc.type = type;
  osc.frequency.value = freq;
  osc.detune.value = detune;
  const gain = ac.createGain();
  gain.gain.setValueAtTime(MIN_GAIN, start);
  gain.gain.exponentialRampToValueAtTime(Math.max(peak, MIN_GAIN), start + attack);
  gain.gain.exponentialRampToValueAtTime(MIN_GAIN, start + attack + release);
  osc.connect(gain);
  if (filterHz) {
    const lp = ac.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = filterHz;
    gain.connect(lp);
    lp.connect(dest);
  } else {
    gain.connect(dest);
  }
  osc.start(start);
  osc.stop(start + attack + release + 0.05);
};

const playNoise = (
  ac: AudioContext,
  dest: AudioNode,
  start: number,
  dur: number,
  peak: number,
  filterHz: number,
): void => {
  const length = Math.max(1, Math.ceil(ac.sampleRate * dur));
  const buffer = ac.createBuffer(1, length, ac.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < length; i++) data[i] = Math.random() * 2 - 1;
  const src = ac.createBufferSource();
  src.buffer = buffer;
  const filter = ac.createBiquadFilter();
  filter.type = 'lowpass';
  filter.frequency.value = filterHz;
  const gain = ac.createGain();
  gain.gain.setValueAtTime(Math.max(peak, MIN_GAIN), start);
  gain.gain.exponentialRampToValueAtTime(MIN_GAIN, start + dur);
  src.connect(filter);
  filter.connect(gain);
  gain.connect(dest);
  src.start(start);
  src.stop(start + dur + 0.02);
};

const playSweep = (
  ac: AudioContext,
  dest: AudioNode,
  start: number,
  fromHz: number,
  toHz: number,
  dur: number,
  peak: number,
  filterHz: number,
): void => {
  const osc = ac.createOscillator();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(fromHz, start);
  osc.frequency.exponentialRampToValueAtTime(Math.max(toHz, MIN_GAIN), start + dur);
  const gain = ac.createGain();
  gain.gain.setValueAtTime(MIN_GAIN, start);
  gain.gain.exponentialRampToValueAtTime(Math.max(peak, MIN_GAIN), start + 0.02);
  gain.gain.exponentialRampToValueAtTime(MIN_GAIN, start + dur);
  const lp = ac.createBiquadFilter();
  lp.type = 'lowpass';
  lp.frequency.value = filterHz;
  osc.connect(gain);
  gain.connect(lp);
  lp.connect(dest);
  osc.start(start);
  osc.stop(start + dur + 0.05);
};

type CueBuilder = (ac: AudioContext, dest: AudioNode, t0: number) => void;

/** Equal-tempered note frequencies (A4 = 440Hz). */
const N = {
  A4: 440,
  C5: 523.25,
  D5: 587.33,
  Eb5: 622.25,
  E5: 659.25,
  F5: 698.46,
  G5: 783.99,
  A5: 880,
  C6: 1046.5,
  E6: 1318.51,
} as const;

const cues: Record<SoundCue, CueBuilder> = {
  [SoundCue.ButtonTap]: (ac, d, t) => {
    playNoise(ac, d, t, 0.022, 0.18, 3200);
    playTone(ac, d, { freq: 1600, type: 'sine', start: t, peak: 0.18, release: 0.045 });
  },
  [SoundCue.WordReveal]: (ac, d, t) => {
    [N.C5, N.E5, N.G5].forEach((f, i) => {
      playTone(ac, d, { freq: f, type: 'sine', start: t + i * 0.07, peak: 0.32, release: 0.18 });
    });
  },
  [SoundCue.AcknowledgeWord]: (ac, d, t) => {
    playTone(ac, d, { freq: N.A5, type: 'sine', start: t, peak: 0.4, release: 0.13 });
  },
  [SoundCue.OpenVote]: (ac, d, t) => {
    for (const f of [N.A4, N.C5, N.E5]) {
      playTone(ac, d, { freq: f, type: 'triangle', start: t, peak: 0.22, release: 0.4 });
    }
  },
  [SoundCue.VoteCast]: (ac, d, t) => {
    playNoise(ac, d, t, 0.04, 0.28, 2400);
    playTone(ac, d, { freq: 880, type: 'sine', start: t, peak: 0.3, release: 0.07 });
  },
  [SoundCue.PlayerEliminated]: (ac, d, t) => {
    playSweep(ac, d, t, 600, 180, 0.6, 0.4, 1400);
    playTone(ac, d, { freq: 110, type: 'sine', start: t, peak: 0.18, release: 0.5 });
  },
  [SoundCue.RoundResult]: (ac, d, t) => {
    playTone(ac, d, { freq: N.D5, type: 'sine', start: t, peak: 0.28, release: 0.16 });
    playTone(ac, d, { freq: N.F5, type: 'sine', start: t + 0.11, peak: 0.28, release: 0.22 });
  },
  [SoundCue.GameWonFlic]: (ac, d, t) => {
    [N.C5, N.E5, N.G5, N.C6].forEach((f, i) => {
      playTone(ac, d, {
        freq: f,
        type: 'triangle',
        start: t + i * 0.09,
        peak: 0.32,
        release: 0.22,
      });
    });
  },
  [SoundCue.GameWonInfiltre]: (ac, d, t) => {
    [N.A4, N.C5, N.E5, N.A5].forEach((f, i) => {
      playTone(ac, d, {
        freq: f,
        type: 'sine',
        start: t + i * 0.1,
        peak: 0.3,
        release: 0.32,
        detune: -10,
      });
    });
  },
  [SoundCue.GameWonClochard]: (ac, d, t) => {
    [N.C5, N.D5, N.E5, N.G5, N.A5, N.E6].forEach((f, i) => {
      playTone(ac, d, {
        freq: f,
        type: 'triangle',
        start: t + i * 0.07,
        peak: 0.28,
        release: 0.16,
      });
    });
  },
  [SoundCue.ClockTick]: (ac, d, t) => {
    playTone(ac, d, { freq: 1200, type: 'sine', start: t, peak: 0.16, release: 0.05 });
  },
  [SoundCue.Error]: (ac, d, t) => {
    playTone(ac, d, { freq: N.E5, type: 'triangle', start: t, peak: 0.3, release: 0.11 });
    playTone(ac, d, { freq: N.C5, type: 'triangle', start: t + 0.11, peak: 0.3, release: 0.16 });
  },
};

const createSynthPlayer = (): AudioPlayer => {
  if (typeof window === 'undefined') return noOpPlayer;
  const Ctor =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return noOpPlayer;

  let state: SynthCtx | null = null;
  let failed = false;

  const ensure = (): SynthCtx | null => {
    if (state) return state;
    if (failed) return null;
    try {
      const ac = new Ctor();
      const master = ac.createGain();
      master.gain.value = 1;
      master.connect(ac.destination);
      state = { ac, master };
      return state;
    } catch {
      failed = true;
      return null;
    }
  };

  return {
    play(cue: SoundCue, options?: PlayOptions): void {
      const volume = options?.volume ?? 1;
      if (volume <= 0) return;
      const ctx = ensure();
      if (!ctx) return;
      try {
        if (ctx.ac.state === 'suspended') {
          const p = ctx.ac.resume();
          if (p && typeof p.catch === 'function') p.catch(() => undefined);
        }
        const builder = cues[cue];
        if (!builder) return;
        const callGain = ctx.ac.createGain();
        callGain.gain.value = Math.min(1, Math.max(0, volume));
        callGain.connect(ctx.master);
        builder(ctx.ac, callGain, ctx.ac.currentTime);
      } catch {
        // AudioContext not ready or builder threw — silent fallback
      }
    },
  };
};

export const synthPlayer: AudioPlayer = createSynthPlayer();

/** Exposed for tests. */
export const _createSynthPlayer = createSynthPlayer;
